import { API_BASE } from '../config';
import { debounce } from 'lodash'; // 자동저장
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import StarRating from './StarRating';
import SurveyAnswerReview from './SurveyAnswerReview';
import { buildSurveySteps, isMissingAnswer, ratingScores } from './surveySteps';
import './pages.css';
import './UserSurveyPage.mobile.css';

const UserSurveyPageRight = ({ selectedSite, onSurveySubmitted, isMobile = false, hidden = false, onBackToList }) => {
    const [questions, setQuestions] = useState([]);
    const [saveStatus, setSaveStatus] = useState({ success: null, time: null }); // null | true | false
    const [answersBySurvey, setAnswersBySurvey] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [uploadingMap, setUploadingMap] = useState({});
    const [stepIndex, setStepIndex] = useState(0);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [retryLoad, setRetryLoad] = useState(0);
    const [navigationError, setNavigationError] = useState('');
    const [busy, setBusy] = useState(false);
    const busyRef = useRef(false);
    const stepHeadingRef = useRef(null);
    const focusStepRef = useRef(false);

    const currentSurveyId = selectedSite?.survey_id;
    const currentAnswers = answersBySurvey[currentSurveyId] || {};
    const questionRefs = useRef({});
        // 새 상태
    const [lockAfterSubmit, setLockAfterSubmit] = useState(1); // 1=제출 후 잠금, 0=수정 가능
    const [editMode, setEditMode] = useState(false);           // 제출 후 "수정" 눌렀는지
    const editBackupRef = useRef(null);                        // 취소용 백업
    const containerRef = useRef(null);
    const latestAnswersRef = useRef({});
    const dirtyRef = useRef(false);
    const saveQueueRef = useRef(Promise.resolve(true));
    const activeSurveyRef = useRef(currentSurveyId);
    activeSurveyRef.current = currentSurveyId;
    const refreshRef = useRef(onSurveySubmitted);
    refreshRef.current = onSurveySubmitted;
    // 조건부 문항 표시
    const visibleQuestions = useMemo(() => {
        return questions.filter((q, index) => {
            const title = String(q.question_text || '')
                .replace(/[‘’'"]/g, '')
                .replace(/\s+/g, '');

            const needsYes = title.includes('예라면');
            const needsNo = title.includes('아니오라면');

            if (!needsYes && !needsNo) return true;

            // 현재 문항 앞쪽의 가장 가까운 객관식 문항을 부모 문항으로 사용
            let parentQuestion = null;
            for (let i = index - 1; i >= 0; i -= 1) {
                if (Number(questions[i].question_type_id) === 2) {
                    parentQuestion = questions[i];
                    break;
                }
            }

            if (!parentQuestion) return true;

            const parentAnswer = String(currentAnswers[parentQuestion.question_id] || '')
                .split('|')[0]
                .trim();

            if (needsYes) return parentAnswer === '예';
            if (needsNo) return parentAnswer === '아니오';

            return true;
        });
    }, [questions, currentAnswers]);

    const steps = useMemo(() => buildSurveySteps(visibleQuestions), [visibleQuestions]);
    const reviewing = reviewOpen || (isSubmitted && !editMode);
    const uploading = Object.values(uploadingMap).some(Boolean);

    useEffect(() => {
        if (isMobile && !hidden && focusStepRef.current) {
            stepHeadingRef.current?.focus({ preventScroll: true });
            window.scrollTo({ top: 0 });
            focusStepRef.current = false;
        }
    }, [isMobile, hidden, stepIndex, reviewing, navigationError]);

    const goToStep = (index) => {
        focusStepRef.current = true;
        setStepIndex(index);
        setReviewOpen(false);
        setNavigationError('');
    };

    const openReview = () => {
        focusStepRef.current = true;
        setReviewOpen(true);
        setNavigationError('');
    };



    // 🔁 자동 저장 로직 (debounce로 감싸기)
const saveAnswers = useCallback((latestAnswers) => {
    const savingSurveyId = currentSurveyId;
    const request = saveQueueRef.current.catch(() => false).then(async () => {
        const token = localStorage.getItem('token');
        const payload = {
        survey_id: savingSurveyId,
        save_data: latestAnswers,
        };
        const isCurrent = () => activeSurveyRef.current === savingSurveyId;
        if (isCurrent()) setSaveStatus({ success: null, time: null, pending: true });
        try {
        const res = await fetch(`${API_BASE}/api/progress/save`, {
            method: 'POST',
            headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
        });

        if (res.ok) {
            if (isCurrent() && latestAnswersRef.current === latestAnswers) {
                dirtyRef.current = false;
                setSaveStatus({ success: true, time: new Date() });
            }
            refreshRef.current?.();
            return true;
        }
        if (isCurrent()) setSaveStatus({ success: false, time: new Date() });
        return false;
        } catch (err) {
            if (isCurrent()) setSaveStatus({ success: false, time: new Date() });
            return false;
        }
    });
    saveQueueRef.current = request;
    return request;
}, [currentSurveyId]);

const debouncedSave = useMemo(() => debounce(saveAnswers, 10000), [saveAnswers]);
useEffect(() => () => { debouncedSave.flush(); }, [debouncedSave]);

const backToList = async () => {
    if (busyRef.current || uploading) return;
    busyRef.current = true;
    setBusy(true);
    debouncedSave.cancel();
    let saved = true;
    if (dirtyRef.current) saved = await saveAnswers(latestAnswersRef.current);
    else await saveQueueRef.current;
    if (saved !== false) {
        setNavigationError('');
        onBackToList?.();
    } else setNavigationError('답변을 저장하지 못했습니다. 연결을 확인한 뒤 목록으로 버튼을 다시 눌러주세요.');
    busyRef.current = false;
    setBusy(false);
};

async function uploadImageFile(file) {
    const token = localStorage.getItem('token');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
    });
    if (!res.ok) throw new Error('upload_fail');
    const j = await res.json();
    return j.url; // '/uploads/xxx.png'
    }

    // --- type6 유틸: 복원 & 제출 변환 ---

// 저장본/제출본에서 type6을 복원: JSON 문자열 배열 → {url,text}[]
function hydrateImageAnswers(rawMap, qs) {
    const next = { ...(rawMap || {}) };
    for (const q of qs || []) {
        if (Number(q.question_type_id) !== 6) continue;
        const raw = next[q.question_id];
        const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
        next[q.question_id] = arr
        .map(v => {
            if (typeof v === 'string') {
            try { return JSON.parse(v); }
            catch { return { url: v, text: '' }; }
            }
            return v; // 이미 객체
        })
        .filter(it => it && it.url);
    }
    return next;
}

// --- type4 유틸: 다중선택 복원 (추가된 부분) ---
function parseMultiSelectAnswers(rawAnswers, qs) {
    const next = { ...(rawAnswers || {}) };
    for (const q of qs || []) {
        if (Number(q.question_type_id) !== 4) continue; 
        
        const raw = next[q.question_id];
        
        if (typeof raw === 'string' && raw.includes('\n')) {
            // 개행 문자로 분리하고, 각 항목의 앞뒤 공백을 제거한 후, 빈 문자열을 제거하여 배열로 만듭니다.
            next[q.question_id] = raw.split('\n')
                .map(item => item.trim())
                .filter(item => item.length > 0);
        } else if (typeof raw === 'string' && raw.length > 0) {
            // 개행 문자가 없는 단일 문자열 응답도 배열로 감싸줍니다.
            next[q.question_id] = [raw.trim()];
        } else if (!Array.isArray(raw)) {
            // 그 외 (null, undefined, 빈 문자열)의 경우 빈 배열로 초기화
            next[q.question_id] = [];
        }
    }
    return next;
}


// 제출용 변환: 질문별로 answer_text 생성
function makeAnswerValue(q, map) {
    const v = map[q.question_id];
    // type6: [{url,text}...] → ['{"url":"..","text":".."}', ...]
    if (Number(q.question_type_id) === 6) {
        const arr = Array.isArray(v) ? v : v ? [v] : [];
        return arr
        .filter(it => it && it.url)
        .map(it => JSON.stringify({ url: it.url, text: it.text || '' }));
    }
    return v;
}


// 수정 버튼
const startEdit = () => {
  // 현재 답안을 백업해 두면 취소 시 되돌릴 수 있어요
    editBackupRef.current = JSON.parse(JSON.stringify(currentAnswers || {}));
    setEditMode(true);
    goToStep(0);
    
  // 특정 컨테이너 스크롤 제어
    if (containerRef.current?.scrollTo) {
        containerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
};

// 취소 버튼
const cancelEdit = () => {
    debouncedSave.cancel();
    dirtyRef.current = false;
    if (editBackupRef.current) {
        setAnswersBySurvey(prev => ({
        ...prev,
        [currentSurveyId]: editBackupRef.current
        }));
        latestAnswersRef.current = editBackupRef.current;
    }
    setEditMode(false);
    setReviewOpen(true);
    setNavigationError('');
};

// 수정제출 (서버는 lock_after_submit === 0에서만 덮어쓰기 허용)
const handleUpdateSubmit = async () => {
    // 기존 handleSubmit과 동일한 payload/검증 로직을 사용
    if (await handleSubmit()) setEditMode(false);
};


    useEffect(() => {
        let cancelled = false;
        setQuestions([]);
        setLoading(Boolean(selectedSite?.survey_id));
        setLoadError('');
        setStepIndex(0);
        setReviewOpen(false);
        setEditMode(false);
        setNavigationError('');
        setSaveStatus({ success: null, time: null });
        setLockAfterSubmit(1);
        dirtyRef.current = false;
        latestAnswersRef.current = {};
        const init = async () => {
            if (!selectedSite?.survey_id) return;
            try {
                const token = localStorage.getItem('token');
                const options = { headers: { Authorization: `Bearer ${token}` } };
                // 사이트 전환 직전의 저장도 반영된 뒤 초깃값을 읽습니다.
                await saveQueueRef.current;
                if (cancelled) return;
                const response = await fetch(`${API_BASE}/api/surveys/${selectedSite.survey_id}/questions`, options);
                const data = await response.json();
                if (!response.ok || !Array.isArray(data)) throw new Error('questions_load_failed');
                if (cancelled) return;
                const metaRes = await fetch(`${API_BASE}/api/surveys/${selectedSite.survey_id}/meta`, options);
                if (!metaRes.ok) throw new Error('meta_load_failed');
                const meta = await metaRes.json();
                const url = selectedSite.status === '완료'
                    ? `${API_BASE}/api/my/responses?survey_id=${selectedSite.survey_id}&site_id=${selectedSite.site_id}`
                    : `${API_BASE}/api/progress/load?survey_id=${selectedSite.survey_id}`;
                const answerRes = await fetch(url, options);
                if (!answerRes.ok) throw new Error('answers_load_failed');
                const result = await answerRes.json();
                if (cancelled) return;
                const hydrated = parseMultiSelectAnswers(hydrateImageAnswers(result.data || {}, data), data);
                setQuestions(data);
                setLockAfterSubmit(Number(meta.lock_after_submit) === 0 ? 0 : 1);
                setAnswersBySurvey(prev => ({ ...prev, [selectedSite.survey_id]: hydrated }));
                latestAnswersRef.current = hydrated;
            } catch (error) {
                if (!cancelled) setLoadError('설문을 불러오지 못했습니다. 연결을 확인하고 다시 시도해주세요.');
            } finally {
                if (!cancelled) setLoading(false);
            }
            };

        init();


        // ✅ 제출 여부 상태 업데이트
        setIsSubmitted(selectedSite?.status === '완료');
        return () => { cancelled = true; };
        }, [selectedSite, retryLoad]);

    const handleChange = (questionId, value) => {
        if (busyRef.current || loading) return;
        if (isSubmitted && !(lockAfterSubmit === 0 && editMode)) return;

        const updatedAnswers = {
            ...latestAnswersRef.current,
            [questionId]: value,
        };
        setAnswersBySurvey((prev) => ({
            ...prev,
            [currentSurveyId]: updatedAnswers
        }));
        latestAnswersRef.current = updatedAnswers;
        dirtyRef.current = true;
        setSaveStatus({ success: null, time: null, pending: true });
        setNavigationError('');
        debouncedSave(updatedAnswers); // ✅ 최신 상태를 반영한 중간 저장
    };

    
    const handleCheckboxChange = (questionId, option) => {
        if (busyRef.current || loading) return;
        if (isSubmitted && !(lockAfterSubmit === 0 && editMode)) return;

    const prevSelections = currentAnswers[questionId] || [];
    const isSelected = prevSelections.some(value => value === option || (option === '기타' && value.startsWith('기타|')));


    const updatedSelections = isSelected
        ? prevSelections.filter((item) => item !== option && !(option === '기타' && item.startsWith('기타|')))
        : [...prevSelections, option];

    const updated = {
        ...currentAnswers,
        [questionId]: updatedSelections
    };

    setAnswersBySurvey((prev) => ({
        ...prev,
        [currentSurveyId]: updated
    }));
    latestAnswersRef.current = updated;
    dirtyRef.current = true;
    setSaveStatus({ success: null, time: null, pending: true });
    setNavigationError('');
    debouncedSave(updated);
    };

    const handleSubmit = async () => {
        if (busyRef.current || uploading || loading || loadError || !steps.length) return false;
        const invalidQuestion = visibleQuestions.find(q => q.required && isMissingAnswer(q, currentAnswers));

        if (invalidQuestion) {
        if (isMobile) {
            goToStep(steps.findIndex(step => step.question.question_id === invalidQuestion.question_id));
            setNavigationError('필수 문항을 입력해주세요.');
        } else alert('필수 문항을 입력해주세요.');
        const el = questionRefs.current[invalidQuestion.question_id];
        if (el?.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return false;
        }

        
        // ✅ [2] 응답 데이터 정리
        // ✅ 응답 데이터 정리 (type6 대응)
        const payloadAnswers = [];
        for (const q of visibleQuestions) {
        const v = makeAnswerValue(q, currentAnswers);
        if (Array.isArray(v)) {
            // type6 또는 다중선택에서 여러 행으로
            v.forEach(item => payloadAnswers.push({
            question_id: q.question_id,
            answer_text: item, // type6이면 '{"url":"..","text":".."}'
            }));
        } else if (v !== undefined) {
            payloadAnswers.push({ question_id: q.question_id, answer_text: v });
        }
        }

        const payload = {
        survey_id: selectedSite.survey_id,
        site_id: selectedSite.site_id,
        answers: payloadAnswers
        };

        // ✅ [3] 제출 요청
        // 대기 중인 자동저장이 제출 뒤에 실행되면 서버 상태가 '진행중'으로 되돌아가므로 먼저 취소
        debouncedSave.cancel();
        busyRef.current = true;
        setBusy(true);
        try {
            // 이미 전송 중인 저장이 제출 이후에 완료되어 상태를 되돌리지 않도록 순서를 보장합니다.
            await saveQueueRef.current;
            const token = localStorage.getItem('token');
            payload.user_id = JSON.parse(atob(token.split('.')[1])).userId;
            const response = await fetch(`${API_BASE}/api/responses`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.success) {
                // 중간 저장 삭제
                await fetch(`${API_BASE}/api/progress/delete`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({ survey_id: selectedSite.survey_id })
                });

                setIsSubmitted(true); // 상태 변경
                dirtyRef.current = false;
                setSaveStatus({ success: null, time: null });
                setReviewOpen(true);
                setNavigationError('');
                if (!isMobile) alert('응답이 성공적으로 제출되었습니다.');
                onSurveySubmitted?.(); // 좌측 갱신하기 위해
                return true;
            } else {
                if (isMobile) setNavigationError('제출하지 못했습니다. ' + (result.error || '다시 시도해주세요.'));
                else alert('제출 실패: ' + result.error);
            }
        } catch (error) {
            setNavigationError('제출하지 못했습니다. 연결을 확인하고 다시 시도해주세요.');
        } finally {
            busyRef.current = false;
            setBusy(false);
        }
        return false;
    };

    if (!selectedSite) {
        return <div className="right-section" hidden={hidden}>📖 버튼을 누르면 설문조사가 표시됩니다.</div>;
    }

        // 제출된 설문이라도 lock_after_submit===0 이고 editMode=true 면 편집 가능
    const readOnly = isSubmitted && !(lockAfterSubmit === 0 && editMode);
    const isDisabled = readOnly || busy || loading;
    const siteUrl = (selectedSite.url || '').trim();
    const selectedIds = steps[stepIndex]?.ids || visibleQuestions.map(q => q.question_id);

    return (
        <div className={`right-section${isMobile ? ' mobile-survey' : ''}`} hidden={hidden} ref={containerRef}>
            {isMobile ? <>
                <header className="survey-mobile-header">
                    <button className="survey-text-button" onClick={backToList} disabled={busy || uploading}>목록으로</button>
                    <span>설문 응답</span>
                    <span className={`survey-save-state${saveStatus.success === false ? ' failed' : ''}`} role="status">
                        {saveStatus.success === false ? '저장 실패' : saveStatus.pending ? '저장 대기 중' : saveStatus.success ? '저장 완료' : '자동저장'}
                    </span>
                </header>
                <div className="survey-mobile-intro">
                    <h1>{selectedSite.site_name || selectedSite.survey_title}</h1>
                    {siteUrl && <a href={/^https?:\/\//i.test(siteUrl) ? siteUrl : `https://${siteUrl}`} target="_blank" rel="noopener noreferrer">사이트 열기 ↗</a>}
                </div>
                {!loading && !loadError && <div className="survey-mobile-progress">
                    <h2 ref={stepHeadingRef} tabIndex={-1}>{reviewing ? (readOnly ? '제출 완료' : '답변 검토') : steps.length ? `문항 ${stepIndex + 1} / ${steps.length}` : '설문 안내'}</h2>
                    {steps.length > 0 && <div className="survey-progress-track" role="progressbar" aria-label="문항 진행" aria-valuemin="0" aria-valuemax={steps.length} aria-valuenow={reviewing ? steps.length : stepIndex + 1}>
                        <span style={{ width: `${(reviewing ? 1 : (stepIndex + 1) / steps.length) * 100}%` }} />
                    </div>}
                </div>}
            </> : <h3>{selectedSite.site_name} - 설문 응답</h3>}

            {loading && <p className="survey-feedback" role="status">설문을 불러오는 중입니다.</p>}
            {loadError && <div className="survey-feedback" role="alert">{loadError} <button className="survey-secondary" onClick={() => setRetryLoad(value => value + 1)}>다시 시도</button></div>}
            {navigationError && <p className="survey-feedback survey-error" role="alert">{navigationError}</p>}
            {saveStatus.success === false && !navigationError && <p className="survey-feedback survey-error" role="alert">자동저장에 실패했습니다. <button className="survey-text-button" disabled={busy} onClick={() => saveAnswers(latestAnswersRef.current)}>다시 저장</button></p>}

            {!isMobile && isSubmitted && (
            lockAfterSubmit === 1 ? (
                // 제출 후 잠금 (빨강)
                <div style={{ fontSize: 18, fontWeight: 'bold', color: 'red', marginBottom: 16 }}>
                제출된 설문조사로 수정이 불가능합니다.
                </div>
            ) : editMode ? (
                // 수정 모드 (노랑)
                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#d97706', marginBottom: 16 }}>
                수정 중입니다.
                </div>
            ) : (
                // 제출 후 수정 가능 (초록)
                <div style={{ fontSize: 18, fontWeight: 'bold', color: '#1a7f37', marginBottom: 16 }}>
                설문이 제출되었습니다. 수정이 가능합니다.
                </div>
            )
            )}


        {visibleQuestions.length > 0 ? (
        (() => {
            let visibleIndex = 0;

            return visibleQuestions.map((q) => {
            const isTextType = Number(q.question_type_id) === 10;

            return (
                <div
                    key={q.question_id}
                    className={`question-card${isTextType ? ' survey-instructions' : ''}`}
                    hidden={isMobile && (reviewing || !selectedIds.includes(q.question_id))}
                    ref={(el) => (questionRefs.current[q.question_id] = el)}
                >
                <div className="question-title" id={`survey-question-${q.question_id}`}>
                    {!isTextType && <span>{++visibleIndex}. </span>}
                    {q.question_text}
                    {Boolean(q.required) && <span style={{ color: 'red', marginLeft: '4px' }}>*</span>}
                </div>

                {q.question_explain && (
                    <div
                    className="question-explain"
                    dangerouslySetInnerHTML={{
                        __html: q.question_explain.replace(/\n/g, '<br>'),
                    }}
                    />
                )}

                {/* 서술형 */}
                {q.question_type_id === 1 && (
                    <textarea
                    className="input-beauty"
                    aria-label={q.question_text}
                    placeholder="여기에 입력하세요..."
                    disabled={isDisabled}
                    value={currentAnswers[q.question_id] || ''}
                    onChange={(e) => handleChange(q.question_id, e.target.value)}
                    />
                )}

                {/* 주관식(단답형) */}
                {Number(q.question_type_id) === 11 && (
                    <input
                    type="text"
                    className="input-beauty"
                    aria-label={q.question_text}
                    placeholder="답변을 입력하세요"
                    disabled={isDisabled}
                    value={currentAnswers[q.question_id] ?? ''}
                    onChange={(e) => handleChange(q.question_id, e.target.value)}
                    />
                )}

                {/* 객관식 */}
                {q.question_type_id === 2 && (
                    <div className="option-list">
                        {q.options?.map((opt, idx) => (
                        <div key={idx} style={{ marginBottom: '4px' }}>
                            <label>
                            <input
                                type="radio"
                                name={`question_${q.question_id}`}
                                disabled={isDisabled}
                                value={opt}
                                checked={Boolean(currentAnswers[q.question_id] === opt || (opt === '기타' && currentAnswers[q.question_id]?.startsWith('기타|')))}
                                onChange={(e) => handleChange(q.question_id, e.target.value)}
                            />{' '}
                            {opt}
                            </label>
                            {opt === '기타' && currentAnswers[q.question_id]?.startsWith('기타') && (
                            <input
                                className="input-beauty"
                                disabled={isDisabled}
                                aria-label="기타 내용"
                                placeholder="기타 내용을 입력하세요"
                                value={currentAnswers[q.question_id]?.split('|')[1] || ''}
                                onChange={(e) =>
                                handleChange(q.question_id, `기타|${e.target.value}`)
                                }
                            />
                            )}
                        </div>
                        ))}
                    </div>
                )}

                {/* 다중선택 */}
                {q.question_type_id === 4 && (
                    <div className="option-list">
                        {q.options?.map((opt, idx) => {
                        const selected = currentAnswers[q.question_id]?.some(value => value === opt || (opt === '기타' && value.startsWith('기타|')));
                        const etcInputValue = currentAnswers[q.question_id]?.find((v) =>
                            v.startsWith('기타|')
                        )?.split('|')[1] || '';

                        return (
                            <div key={idx} style={{ marginBottom: '4px' }}>
                            <label>
                                <input
                                type="checkbox"
                                name={`question_${q.question_id}`}
                                disabled={isDisabled}
                                checked={Boolean(selected)}
                                onChange={() => handleCheckboxChange(q.question_id, opt)}
                                />{' '}
                                {opt}
                            </label>
                            {opt === '기타' && selected && (
                                <input
                                className="input-beauty"
                                disabled={isDisabled}
                                aria-label="기타 내용"
                                placeholder="기타 내용을 입력하세요"
                                value={etcInputValue}
                                onChange={(e) => {
                                    const existing = currentAnswers[q.question_id]?.filter(
                                    (v) => v !== '기타' && !v.startsWith('기타|')
                                    ) || [];
                                    const updated = [...existing, `기타|${e.target.value}`];
                                    handleChange(q.question_id, updated);
                                }}
                                />
                            )}
                            </div>
                        );
                        })}
                    </div>
                )}

                {/* 이미지 업로드형 (여러 장) */}
                {q.question_type_id === 6 && (() => {
                const list = Array.isArray(currentAnswers[q.question_id]) ? currentAnswers[q.question_id] : [];

                // 리스트를 업데이트하면서 자동 저장(=handleChange)까지 한 번에
                const setList = (updater) => {
                    const base = Array.isArray(latestAnswersRef.current[q.question_id]) ? latestAnswersRef.current[q.question_id] : [];
                    const next = updater(base);
                    handleChange(q.question_id, next);
                };

                return (
                    <div className="img-multi">
                    <input
                        type="file"
                        aria-label={`${q.question_text} 이미지 첨부`}
                        accept="image/*"
                        multiple
                        disabled={isDisabled || uploading}
                        onChange={async (e) => {
                        const input = e.target;
                        const files = Array.from(e.target.files || []);
                        if (!files.length) return;
                        const uploadSurveyId = currentSurveyId;
                        setUploadingMap(prev => ({ ...prev, [q.question_id]: true }));
                        const added = [];
                        let failures = 0;
                        for (const f of files) {
                            try {
                            const url = await uploadImageFile(f);
                            added.push({ url, text: '' });
                            } catch { failures++; }
                        }
                        if (activeSurveyRef.current === uploadSurveyId) {
                            if (added.length) setList(arr => [...arr, ...added]);
                            if (failures) setNavigationError(`이미지 ${failures}개를 올리지 못했습니다. 다시 첨부해주세요.`);
                        }
                        setUploadingMap(prev => ({ ...prev, [q.question_id]: false }));
                        input.value = '';
                        }}
                    />
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:12, marginTop:10 }}>
                        {list.map((item, idx) => (
                        <div key={idx} style={{ border:'1px solid #eee', borderRadius:8, padding:8 }}>
                            <img
                            src={item.url.startsWith('http') ? item.url : `${API_BASE}${item.url}`}
                            alt=""
                            style={{ width:'100%', height:120, objectFit:'cover', borderRadius:6 }}
                            />
                            <input
                            className="input-beauty"
                            placeholder="설명(선택)"
                            disabled={isDisabled}
                            value={item.text || ''}
                            onChange={(e) => setList(arr => {
                                const next = [...arr]; next[idx] = { ...next[idx], text: e.target.value }; return next;
                            })}
                            style={{ marginTop: 6 }}
                            />
                            <button
                            className="edit-delete"
                            disabled={isDisabled}
                            onClick={() => setList(arr => arr.filter((_, i) => i !== idx))}
                            style={{ marginTop: 6, width: '100%' }}
                            >
                            삭제🗑️
                            </button>
                        </div>
                        ))}
                    </div>
                    </div>
                );
                })()}

                
                {/* {q.question_type_id === 6 && (() => {
                const parseImgAnswer = (v) => {
                    try { const o = JSON.parse(v || '{}'); return { url: o.url || '', text: o.text || '' }; }
                    catch { return { url: '', text: '' }; }
                };
                const makeAbs = (u) => (u?.startsWith('http') ? u : `${API_BASE}${u || ''}`);

                const { url, text } = parseImgAnswer(currentAnswers[q.question_id]);
                const uploading = !!uploadingMap[q.question_id];

                return (
                    <div className="option-list" style={{ display: 'grid', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <input
                        type="file"
                        accept="image/*"
                        disabled={isDisabled || uploading}
                        onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                            setUploadingMap((m) => ({ ...m, [q.question_id]: true }));
                            const fd = new FormData();
                            fd.append('file', file);
                            const token = localStorage.getItem('token');
                            const res = await fetch(`${API_BASE}/api/upload`, {
                                method: 'POST',
                                headers: { Authorization: `Bearer ${token}` },
                                body: fd,
                            });
                            const j = await res.json();
                            if (!j.success) throw new Error('upload_failed');
                            // JSON 문자열로 저장
                            const next = JSON.stringify({ url: j.url, text });
                            handleChange(q.question_id, next);
                            } catch (err) {
                            alert('이미지 업로드에 실패했습니다.');
                            } finally {
                            setUploadingMap((m) => ({ ...m, [q.question_id]: false }));
                            }
                        }}
                        />
                        {uploading && <span className="sub-muted">업로드 중…</span>}
                        {!!url && !uploading && (
                        <a href={makeAbs(url)} target="_blank" rel="noreferrer" className="question-explain">
                            업로드 이미지 보기
                        </a>
                        )}
                    </div>

                    {!!url && !uploading && (
                        <img
                        src={makeAbs(url)}
                        alt="업로드 이미지"
                        style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, border: '1px solid #eee' }}
                        />
                    )}

                    <textarea
                        className="input-beauty"
                        rows={3}
                        disabled={isDisabled}
                        placeholder="여기에 멘트를 작성해 주세요"
                        value={text}
                        onChange={(e) => {
                        const next = JSON.stringify({ url, text: e.target.value });
                        handleChange(q.question_id, next);
                        }}
                    />
                    </div>
                );
                })()} */}

                {/* 별점 */}
                {q.question_type_id === 3 && (isMobile ? (
                    <label className="survey-rating-label">별점 선택
                        <select className="input-beauty" aria-label={q.question_text} disabled={isDisabled} value={currentAnswers[q.question_id] || ''} onChange={e => handleChange(q.question_id, e.target.value === '' ? undefined : Number(e.target.value))}>
                            <option value="">선택해주세요</option>
                            {ratingScores(q).map(score => <option key={score} value={score}>{score}점</option>)}
                        </select>
                    </label>
                ) : <StarRating
                    value={currentAnswers[q.question_id]} // ✅ 별점 불러오기
                    max={q.max_score}
                    step={q.step}
                    disabled={isDisabled}
                    onChange={(value) => handleChange(q.question_id, parseFloat(value))}
                    />
                )}
                </div>
            );
            });
        })()
        ) : (
        !loading && !loadError && <p className="survey-feedback">문항이 없습니다.</p>
        )}

            
            {isMobile && reviewing && !loading && !loadError && <SurveyAnswerReview steps={steps} answers={currentAnswers} readOnly={readOnly} onEdit={goToStep} />}
            {isMobile && readOnly && !loading && !loadError && <p className="survey-feedback">{lockAfterSubmit === 0 ? '제출한 답변을 수정할 수 있습니다.' : '제출한 설문은 수정할 수 없습니다.'}</p>}
            {isMobile && uploading && <p className="survey-feedback" role="status">이미지를 올리는 중입니다. 완료될 때까지 기다려주세요.</p>}
            {isMobile && !loading && !loadError && steps.length > 0 && <div className="survey-mobile-actions">
                {!readOnly ? <>
                    <div className="survey-mobile-action-row">
                        <button className="survey-secondary" disabled={busy || uploading || (!reviewing && stepIndex === 0)} onClick={() => goToStep(reviewing ? steps.length - 1 : stepIndex - 1)}>이전</button>
                        {reviewing ? <button className="survey-primary" disabled={busy || uploading} onClick={editMode ? handleUpdateSubmit : handleSubmit}>{busy ? '제출 중…' : editMode ? '수정 제출' : '설문 제출'}</button>
                        : <button className="survey-primary" disabled={busy || uploading} onClick={() => stepIndex < steps.length - 1 ? goToStep(stepIndex + 1) : openReview()}>{stepIndex < steps.length - 1 ? '다음 문항' : '답변 검토'}</button>}
                    </div>
                    {editMode && <button className="survey-text-button" disabled={busy || uploading} onClick={cancelEdit}>수정 취소</button>}
                </> : lockAfterSubmit === 0 && <button className="survey-primary" onClick={startEdit}>수정</button>}
            </div>}

            {!isMobile && saveStatus.time && (
            <p style={{ fontSize: '12px', marginTop: '10px', color: saveStatus.success ? 'green' : 'red' }}>
                자동 저장 {saveStatus.success ? '성공' : '실패'} ({saveStatus.time.toLocaleTimeString()})
            </p>
            )}

            {/* 제출 전 */}
            {!isMobile && !isSubmitted && !loading && !loadError && (
            <button
                className="user-button"
                disabled={busy || uploading}
                onClick={async () => {
                await handleSubmit();
                }}
            >
                제출
            </button>
            )}

            {/* 제출 후 수정 가능(lock_after_submit === 0) & 아직 수정 중 아님 */}
            {!isMobile && isSubmitted && lockAfterSubmit === 0 && !editMode && (
            <button className="user-button" onClick={startEdit}>
                수정
            </button>
            )}

            {/* 제출 후 + 수정 모드 */}
            {!isMobile && isSubmitted && lockAfterSubmit === 0 && editMode && (
            <div style={{ display: 'flex', gap: 8 }}>
                <button className="user-button" disabled={busy || uploading} onClick={async () => {
                await handleUpdateSubmit();
                }}>
                수정제출
                </button>
                <button className="user-button secondary" disabled={busy || uploading} onClick={cancelEdit}>
                취소
                </button>
            </div>
            )}

        </div>
    );
};

export default UserSurveyPageRight;
